import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import imageCompression from 'browser-image-compression';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmployeeDay = ({ useShortCode = false, useEmployeeId = false }) => {
  const { token, shortcode, employeeId } = useParams();
  const actualToken = useShortCode ? shortcode : (useEmployeeId ? employeeId : token);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [dailyCitation, setDailyCitation] = useState(null);
  
  // Stocker le token JWT complet reçu depuis l'API pour les uploads
  const [fullToken, setFullToken] = useState(null);
  
  // État pour le média employé (Photo/GIF/Vidéo)
  const [employeeMedia, setEmployeeMedia] = useState(null);
  const [showMediaVideo, setShowMediaVideo] = useState(false);
  
  // États pour le récapitulatif mensuel
  const [monthlyMissions, setMonthlyMissions] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showMonthlyRecap, setShowMonthlyRecap] = useState(false);
  
  // États pour la gestion des saisies locales
  const [editingParures, setEditingParures] = useState({});
  const [editingInformations, setEditingInformations] = useState({});
  const [savingStates, setSavingStates] = useState({});
  
  // État pour gérer l'expansion/collapse des missions
  const [expandedMissions, setExpandedMissions] = useState({});
  
  // États pour la gestion des photos
  const [uploadingPhotos, setUploadingPhotos] = useState({});
  const [uploadedPhotos, setUploadedPhotos] = useState({});
  const [photosPreviews, setPhotosPreviews] = useState({});
  
  // États pour les pré-commandes - SUPPRIMÉ (détection automatique)
  
  // États pour les emojis
  const [showEmojiPicker, setShowEmojiPicker] = useState(null);
  
  // États pour le contrôle des produits
  const [showProductCheck, setShowProductCheck] = useState({});
  const [missingProducts, setMissingProducts] = useState({});
  const [savingProducts, setSavingProducts] = useState({});
  const [catalogProducts, setCatalogProducts] = useState([]);
  
  const fileInputRefs = useRef({});
  
  // Catégoriser les produits du catalogue par pièce
  const categorizeProduct = (productName) => {
    const name = productName.toLowerCase();
    
    // Cuisine
    if (name.includes('café') || name.includes('capsule') || name.includes('thé') || name.includes('sachet') ||
        name.includes('sucre') || name.includes('huile') || name.includes('vinaigre bals') ||
        name.includes('poivre') || name.includes('sel ') || name.includes('liquide vaisselle') ||
        name.includes('pastille') || name.includes('rinçage') || name.includes('lave-vaisselle')) {
      return "🍳 Cuisine";
    }
    
    // Salle de bain
    if (name.includes('gel douche') || name.includes('shampoo') || name.includes('shampoing') ||
        name.includes('savon') || name.includes('canard') || name.includes('wc') ||
        name.includes('papier toilette') || name.includes('pq') || name.includes('serviette') ||
        name.includes('tapis bain')) {
      return "🚿 Salle de bain";
    }
    
    // Ménage
    if (name.includes('lessive') || name.includes('dosette') || name.includes('éponge') ||
        name.includes('fébreze') || name.includes('febreze') || name.includes('javel') ||
        name.includes('vinaigre') || name.includes('produit sol') || name.includes('sac poubelle') ||
        name.includes('sac 50') || name.includes('sac 100') || name.includes('sac sdb') ||
        name.includes('désodorisant') || name.includes('torchon')) {
      return "🧹 Ménage";
    }
    
    // Papeterie
    if (name.includes('sopalin') || name.includes('essuie') || name.includes('essui')) {
      return "📄 Papeterie";
    }
    
    // Linge
    if (name.includes('drap') || name.includes('housse') || name.includes('taie') || name.includes('couette')) {
      return "🛏️ Linge";
    }
    
    // Par défaut
    return "📦 Autres";
  };
  
  // Organiser les produits du catalogue par pièce
  const getProductsByRoom = () => {
    const grouped = {};
    
    catalogProducts.forEach(product => {
      const room = categorizeProduct(product.nom_produit);
      if (!grouped[room]) {
        grouped[room] = [];
      }
      grouped[room].push(product.nom_produit);
    });
    
    // Trier les pièces dans un ordre logique
    const orderedRooms = ["🍳 Cuisine", "🚿 Salle de bain", "🧹 Ménage", "📄 Papeterie", "🛏️ Linge", "📦 Autres"];
    const result = {};
    orderedRooms.forEach(room => {
      if (grouped[room] && grouped[room].length > 0) {
        result[room] = grouped[room].sort();
      }
    });
    
    return result;
  };

  useEffect(() => {
    loadEmployeeDay();
  }, [actualToken]);

  // Charger le catalogue une fois qu'on connaît l'employé (résolution tenant côté back)
  useEffect(() => {
    if (data?.employee?.id) {
      loadCatalog(data.employee.id);
    }
  }, [data?.employee?.id]);
  
  // Charger le catalogue de produits (passe employee_id pour la résolution tenant public)
  const loadCatalog = async (employeeId) => {
    try {
      const params = employeeId ? { params: { employee_id: employeeId } } : {};
      const response = await axios.get(`${API}/product-catalog`, params);
      setCatalogProducts(response.data || []);
    } catch (error) {
      console.error("Erreur chargement catalogue:", error);
    }
  };
  
  // Calculer le total des photos en cours d'upload
  const totalUploading = Object.values(uploadingPhotos).reduce((sum, count) => sum + count, 0);
  
  // Avertir avant de fermer si des uploads sont en cours
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (totalUploading > 0) {
        e.preventDefault();
        e.returnValue = '⚠️ Des photos sont en cours d\'envoi. Êtes-vous sûr de vouloir quitter ?';
        return e.returnValue;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [totalUploading]);

  const loadEmployeeDay = async () => {
    setLoading(true);
    setError(null);
    try {
      // Utiliser le endpoint approprié selon le mode :
      //  - useShortCode      → /e/{shortcode}     (lien court)
      //  - useEmployeeId     → /employee-day-by-id/{employeeId} (lien /planning-employe/)
      //  - défaut            → /employee-day/{token}
      const endpoint = useShortCode
        ? `${API}/e/${actualToken}`
        : (useEmployeeId
            ? `${API}/employee-day-by-id/${actualToken}`
            : `${API}/employee-day/${actualToken}`);
      const response = await axios.get(endpoint);
      setData(response.data);
      
      // Stocker le token JWT complet reçu depuis l'API
      if (response.data.full_token) {
        setFullToken(response.data.full_token);
      }
      
      // Initialiser l'état d'expansion : TOUTES les missions = collapsed par défaut
      const initialExpanded = {};
      response.data.missions.forEach(mission => {
        // Toutes les missions sont contractées par défaut (false)
        initialExpanded[mission.id] = false;
      });
      setExpandedMissions(initialExpanded);
      
      // Charger les missions mensuelles pour le récapitulatif
      const employeeId = response.data.employee.id;
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;
      try {
        const monthlyResponse = await axios.get(`${API}/employee/${employeeId}/missions-month/${year}/${month}`);
        setMonthlyMissions(monthlyResponse.data || []);
      } catch (monthlyError) {
        console.log("Erreur chargement missions mensuelles:", monthlyError);
        setMonthlyMissions([]);
      }
      
      // Charger la citation du jour pour cet employé (si éligible)
      const employeePrenom = response.data.employee.prenom;
      const employeePrenomLower = employeePrenom.toLowerCase();
      const eligibleEmployees = ['cyril', 'marion', 'magali', 'ben'];
      
      // Charger le média de l'employé (Photo/GIF/Vidéo) si disponible
      try {
        const mediaResponse = await axios.get(`${API}/employee-medias/${employeeId}`);
        if (mediaResponse.data && mediaResponse.data.media_data) {
          setEmployeeMedia(mediaResponse.data);
        } else {
          setEmployeeMedia(null);
        }
      } catch (mediaError) {
        // Pas de média pour cet employé
        setEmployeeMedia(null);
      }
      
      // Vérifier si l'employé est éligible (exact match ou contient "m&m")
      const isEligible = eligibleEmployees.includes(employeePrenomLower) || employeePrenomLower.includes('m&m');
      
      if (isEligible) {
        try {
          // Pour M&M, utiliser un alias sans & pour éviter les problèmes d'URL
          const prenomForUrl = employeePrenomLower.includes('m&m') 
            ? 'MMCONCIERGERIE' 
            : employeePrenom;
          
          const citationResponse = await axios.get(
            `${API}/citations/daily/${prenomForUrl}/${response.data.mission_date}`
          );
          setDailyCitation(citationResponse.data);
        } catch (citationError) {
          console.log("Pas de citation disponible:", citationError);
        }
      }
      
    } catch (error) {
      console.error("Erreur:", error);
      setError(error.response?.data?.detail || "Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };
  
  // Recharger les missions mensuelles quand le mois change
  const loadMonthlyMissions = async () => {
    if (!data?.employee?.id) return;
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth() + 1;
    try {
      const response = await axios.get(`${API}/employee/${data.employee.id}/missions-month/${year}/${month}`);
      setMonthlyMissions(response.data || []);
    } catch (error) {
      console.log("Erreur chargement missions mensuelles:", error);
    }
  };
  
  // Effet pour recharger quand le mois change
  React.useEffect(() => {
    if (data?.employee?.id) {
      loadMonthlyMissions();
    }
  }, [currentMonth]);
  
  // Fonctions pour le récapitulatif mensuel
  const changeMonth = (delta) => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + delta);
      return newDate;
    });
  };
  
  const getMonthName = () => {
    return currentMonth.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };
  
  const getMissionsByDate = () => {
    const grouped = {};
    monthlyMissions.forEach(m => {
      const date = m.mission?.date_mission || m.date_mission;
      if (!date) return;
      if (!grouped[date]) grouped[date] = [];
      grouped[date].push(m);
    });
    return Object.entries(grouped).sort(([a], [b]) => new Date(a) - new Date(b));
  };
  
  const isDatePast = (dateStr) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const date = new Date(dateStr);
    return date < today;
  };
  
  const isDateToday = (dateStr) => {
    const today = new Date().toISOString().split('T')[0];
    return dateStr === today;
  };
  
  const formatDateShort = (dateStr) => {
    const date = new Date(dateStr);
    const days = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
    return `${days[date.getDay()]} ${date.getDate()}`;
  };
  
  // Fonction pour calculer le prix selon l'employé
  // RÈGLE STRICTE : aucun fallback entre colonnes ni prix par défaut
  // - Propriétaire  → mission.prix / housing.prix_mm_conciergerie / 0
  // - Intervenant   → mission.prix_intervenant_exceptionnel / housing.prix_intervenant / 0
  const getMissionPriceForEmployee = (missionData) => {
    const mission = missionData.mission || missionData;
    const housing = missionData.housing || {};
    if (!housing || Object.keys(housing).length === 0) return 0;

    const employee = data?.employee || {};
    const typeEmploye = (employee.type_employe || '').toLowerCase().trim();
    const prenom = (employee.prenom || '').toLowerCase().trim();
    const nom = (employee.nom || '').toLowerCase().trim();

    const isOwnerByType = typeEmploye === 'proprietaire';
    const isMagOrMarionRoux = (
      prenom === 'mag' ||
      prenom === 'magali' ||
      prenom.includes('mag') ||
      (prenom === 'marion' && nom === 'roux') ||
      (prenom.includes('marion') && nom.includes('roux')) ||
      prenom.includes('m&m') ||
      prenom === 'mm'
    );
    const isOwner = isOwnerByType || isMagOrMarionRoux;

    // BINÔME : prix_binome uniquement (pas de fallback)
    if (mission.en_binome && mission.binome_employee_id === employee.id) {
      return mission.prix_binome || 0;
    }

    if (isOwner) {
      if (mission.prix != null && mission.prix !== '') return mission.prix;
      if (housing.prix_mm_conciergerie != null && housing.prix_mm_conciergerie !== '') return housing.prix_mm_conciergerie;
      return 0;
    }

    // INTERVENANT
    if (mission.prix_intervenant_exceptionnel != null && mission.prix_intervenant_exceptionnel !== '') {
      return mission.prix_intervenant_exceptionnel;
    }
    if (housing.prix_intervenant != null && housing.prix_intervenant !== '') {
      return housing.prix_intervenant;
    }
    return 0;
  };
  
  const calculateMonthlyTotal = () => {
    return monthlyMissions.reduce((total, m) => {
      return total + getMissionPriceForEmployee(m);
    }, 0);
  };
  
  const calculateCompletedTotal = () => {
    return monthlyMissions
      .filter(m => (m.mission?.status || m.status) === 'effectue')
      .reduce((total, m) => {
        return total + getMissionPriceForEmployee(m);
      }, 0);
  };
  
  const totalMonthlyMissions = monthlyMissions.length;
  const completedMonthlyMissions = monthlyMissions.filter(m => (m.mission?.status || m.status) === 'effectue').length;
  const pendingMonthlyMissions = totalMonthlyMissions - completedMonthlyMissions;

  // Fonction supprimée - logement_pret maintenant géré par markMissionCompleted
  
  // Basculer l'expansion/collapse d'une mission
  const toggleMissionExpanded = (missionId) => {
    setExpandedMissions(prev => ({
      ...prev,
      [missionId]: !prev[missionId]
    }));
  };
  
  // Toggle affichage contrôle produits
  const toggleProductCheck = (missionId) => {
    setShowProductCheck(prev => ({
      ...prev,
      [missionId]: !prev[missionId]
    }));
  };
  
  // Cocher/décocher un produit manquant
  const toggleMissingProduct = (missionId, product) => {
    setMissingProducts(prev => {
      const currentMissing = prev[missionId] || [];
      if (currentMissing.includes(product)) {
        return {
          ...prev,
          [missionId]: currentMissing.filter(p => p !== product)
        };
      } else {
        return {
          ...prev,
          [missionId]: [...currentMissing, product]
        };
      }
    });
  };
  
  // Envoyer automatiquement un produit manquant (pré-commande silencieuse)
  const sendMissingProduct = async (missionId, housingId, housingName, product, isAdding) => {
    try {
      await axios.post(`${API}/stock-manquant/auto`, {
        mission_id: missionId,
        housing_id: housingId,
        housing_name: housingName,
        employee_id: data.employee.id,
        employee_name: data.employee.prenom,
        date: data.mission_date,
        produit: product,
        action: isAdding ? 'add' : 'remove'
      });
      console.log(`Produit ${isAdding ? 'ajouté' : 'retiré'}: ${product}`);
    } catch (error) {
      console.error("Erreur envoi produit:", error);
    }
  };
  
  // Toggle produit manquant avec envoi automatique
  const toggleMissingProductAuto = (missionId, housingId, housingName, product) => {
    const currentMissing = missingProducts[missionId] || [];
    const isCurrentlyChecked = currentMissing.includes(product);
    
    // Mettre à jour l'état local
    if (isCurrentlyChecked) {
      setMissingProducts(prev => ({
        ...prev,
        [missionId]: currentMissing.filter(p => p !== product)
      }));
    } else {
      setMissingProducts(prev => ({
        ...prev,
        [missionId]: [...currentMissing, product]
      }));
    }
    
    // Envoyer automatiquement au serveur
    sendMissingProduct(missionId, housingId, housingName, product, !isCurrentlyChecked);
  };

  const handleInformationsChange = async (missionId, value) => {
    // Sauvegarder uniquement localement
    setEditingInformations(prev => ({
      ...prev,
      [missionId]: value
    }));
  };

  const insertEmoji = (missionId, emoji) => {
    const currentValue = editingInformations[missionId] || '';
    setEditingInformations(prev => ({
      ...prev,
      [missionId]: currentValue + emoji
    }));
    setShowEmojiPicker(null);
  };

  // Fonction supprimée - informations_employe maintenant géré par markMissionCompleted

  const handleParuresSalesChange = async (missionId, value) => {
    // Sauvegarder uniquement localement
    setEditingParures(prev => ({
      ...prev,
      [missionId]: value
    }));
  };

  // Fonction supprimée - parures_sales maintenant géré par markMissionCompleted

  // Ajouter une note à la prochaine mission du même logement
  const addNoteToNextMission = async (missionId, housingId) => {
    const remarque = editingInformations[missionId];
    
    if (!remarque || remarque.trim() === '') {
      toast.warning("Veuillez entrer une remarque à transférer");
      return;
    }
    
    if (!housingId) {
      toast.warning("Cette fonctionnalité n\'est disponible que pour les missions avec logement");
      return;
    }
    
    setSavingStates(prev => ({ ...prev, [`note_${missionId}`]: 'saving' }));
    
    try {
      const response = await axios.post(
        `${API}/missions/${missionId}/add-note-to-next`,
        null,
        { params: { note: remarque.trim() } }
      );
      
      setSavingStates(prev => ({ ...prev, [`note_${missionId}`]: 'validated' }));
      toast.success(`${response.data.message}`);
      
      // Reset après 3 secondes
      setTimeout(() => {
        setSavingStates(prev => ({ ...prev, [`note_${missionId}`]: null }));
      }, 3000);
      
    } catch (error) {
      console.error('Erreur ajout note:', error);
      setSavingStates(prev => ({ ...prev, [`note_${missionId}`]: 'error' }));
      toast.error(error.response?.data?.detail || "Erreur lors de l'ajout de la note");
      
      setTimeout(() => {
        setSavingStates(prev => ({ ...prev, [`note_${missionId}`]: null }));
      }, 3000);
    }
  };

  const markMissionCompleted = async (missionId, mission, housing) => {
    // PROTECTION: Bloquer si des photos sont en cours d'envoi
    const photosEnCours = uploadingPhotos[missionId] || 0;
    if (photosEnCours > 0) {
      toast.warning(`⏳ Attendez la fin de l'envoi des ${photosEnCours} photo(s)`);
      return;
    }
    
    // Récupérer les valeurs locales ou du serveur
    const paruresSalesValue = editingParures[missionId] !== undefined
      ? editingParures[missionId]
      : mission.parures_sales;
    
    const gestionParuresActive = housing ? (housing.gestion_parures !== false && housing.type_client !== 'professionnel') : false;
    
    // VALIDATION: Si gestion parures active, le champ DOIT être rempli
    if (gestionParuresActive && (paruresSalesValue === undefined || paruresSalesValue === null || paruresSalesValue === '')) {
      toast.warning('⚠️ Indiquez le nombre de parures sales avant de valider');
      return;
    }
    
    const numParures = paruresSalesValue === '' ? 0 : parseInt(paruresSalesValue) || 0;
    
    // Récupérer les remarques locales ou du serveur
    const remarquesValue = editingInformations[missionId] !== undefined
      ? editingInformations[missionId]
      : mission.informations_employe || '';
    
    setSavingStates(prev => ({ ...prev, [`completed_${missionId}`]: 'saving' }));
    
    try {
      // Paramètres d'auth publique : `token` (lien /e/, /employee-day/) OU `employee_id` (lien /planning-employe/)
      const authParams = useEmployeeId
        ? { employee_id: actualToken }
        : (fullToken ? { token: fullToken } : {});

      // 1. Valider les remarques et logement_pret = true
      await axios.patch(`${API}/missions/${missionId}/logement-status`, {
        informations_employe: remarquesValue,
        logement_pret: housing ? true : undefined
      }, {
        params: authParams
      });
      
      // 2. Détection automatique DÉSACTIVÉE - Les produits sont gérés via le contrôle stock
      // L'employé utilise maintenant la section "Contrôle des produits" pour signaler les manquants
      
      // 3. Valider parures_sales (si gestion parures active)
      if (gestionParuresActive) {
        await axios.patch(`${API}/missions/${missionId}/parures-sales`, null, {
          params: {
            parures_sales: numParures,
            ...authParams
          }
        });
        
        // Nettoyer l'état local
        setEditingParures(prev => {
          const newState = { ...prev };
          delete newState[missionId];
          return newState;
        });
      }
      
      // 4. Nettoyer l'état local des remarques
      setEditingInformations(prev => {
        const newState = { ...prev };
        delete newState[missionId];
        return newState;
      });
      
      // 5. Marquer la mission comme effectuée
      await axios.patch(`${API}/missions/${missionId}/complete`, null, {
        params: authParams
      });
      
      // Marquer comme validé
      setSavingStates(prev => ({ ...prev, [`completed_${missionId}`]: 'validated' }));
      
      // 6. Masquer automatiquement les détails de la mission
      setExpandedMissions(prev => ({
        ...prev,
        [missionId]: false
      }));
      
      // Recharger les données
      await loadEmployeeDay();
    } catch (error) {
      console.error("Erreur marquage mission effectuée:", error);
      setSavingStates(prev => ({ ...prev, [`completed_${missionId}`]: 'error' }));
      toast.error("Erreur lors de la validation de la mission");
      
      setTimeout(() => {
        setSavingStates(prev => {
          const newState = { ...prev };
          delete newState[`completed_${missionId}`];
          return newState;
        });
      }, 3000);
    }
  };

  // Gestion des photos - UPLOAD DIRECT SANS LIMITE
  const handlePhotoCapture = async (missionId, event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;
    
    console.log(`📷 Upload: ${files.length} photo(s) pour mission ${missionId}`);
    
    // Pas de limite - uploader toutes les photos
    const filesToProcess = files;
    
    // Compteur d'upload
    setUploadingPhotos(prev => ({ ...prev, [missionId]: filesToProcess.length }));
    
    let successCount = 0;
    let errorCount = 0;
    
    // Upload séquentiel
    for (let i = 0; i < filesToProcess.length; i++) {
      const file = filesToProcess[i];
      
      try {
        // Compression
        let fileToUpload = file;
        try {
          fileToUpload = await imageCompression(file, {
            maxSizeMB: 1.5,
            maxWidthOrHeight: 1920,
            useWebWorker: true
          });
        } catch (e) {
          console.warn('Compression échouée, utilisation originale');
        }
        
        // Convertir en base64 pour l'endpoint employé
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(fileToUpload);
        });
        
        // Utiliser l'endpoint approprié selon le mode
        //  - useEmployeeId : /employee-day-by-id/{id}/upload-photo (pas de token)
        //  - autres        : /employee-day/{token}/upload-photo
        const uploadUrl = useEmployeeId
          ? `${API}/employee-day-by-id/${actualToken}/upload-photo`
          : `${API}/employee-day/${fullToken || actualToken}/upload-photo`;
        const response = await axios.post(
          uploadUrl,
          { 
            mission_id: missionId,
            photo: base64
          },
          { timeout: 120000 }  // 2 minutes timeout
        );
        
        if (response.data?.success) {
          successCount++;
          setUploadedPhotos(prev => ({ ...prev, [missionId]: (prev[missionId] || 0) + 1 }));
          console.log(`✅ Photo ${i + 1}/${filesToProcess.length} uploadée`);
        } else {
          throw new Error('Upload failed');
        }
        
      } catch (error) {
        errorCount++;
        console.error(`❌ Erreur photo ${i + 1}:`, error.response?.data?.detail || error.message);
      }
      
      // Mettre à jour le compteur d'upload en cours
      setUploadingPhotos(prev => ({ 
        ...prev, 
        [missionId]: Math.max(0, filesToProcess.length - i - 1) 
      }));
    }
    
    // Notification finale
    if (errorCount > 0) {
      toast.info(`📷 ${successCount} photo(s) envoyée(s), ${errorCount} erreur(s)`);
    } else if (successCount > 0) {
      toast.success(`${successCount} photo(s) envoyée(s) avec succès`);
    }
    
    setUploadingPhotos(prev => ({ ...prev, [missionId]: 0 }));
    event.target.value = '';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex justify-center items-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex justify-center items-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-md">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-red-600 mb-2">Erreur</h2>
          <p className="text-gray-600">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="mt-6 bg-red-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-red-700"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  const { employee, mission_date, missions } = data;
  
  // Trier les missions : missions non effectuées en haut, effectuées en bas
  const sortedMissions = [...missions].sort((a, b) => {
    // Si a est effectuée et b non, a va en bas (retourne 1)
    if (a.status === 'effectue' && b.status !== 'effectue') return 1;
    // Si b est effectuée et a non, b va en bas (retourne -1)
    if (b.status === 'effectue' && a.status !== 'effectue') return -1;
    // Sinon, garder l'ordre original
    return 0;
  });
  
  // Séparer les missions ménage et DIVERS
  const missionsMenage = sortedMissions.filter(m => m.housing);
  const missionsDivers = sortedMissions.filter(m => !m.housing);
  
  // Fonction pour obtenir le titre personnalisé
  const getGreeting = () => {
    const prenomLower = employee.prenom.toLowerCase();
    if (prenomLower === 'marion' || prenomLower === 'magali') {
      return '👋 Bonjour Patronne !';
    } else if (prenomLower === 'm&m') {
      return '👋 Bonjour les Patronnes !';
    } else {
      return `👋 Bonjour ${employee.prenom} !`;
    }
  };

  const commonEmojis = ['👍', '👎', '⚠️', '✅', '❌', '💡', '🔑', '🚪', '🪟', '🧹', '🧴', '📦', '🔌', '💧'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 pb-20">
      {/* Indicateur d'upload en cours - Fixe en haut */}
      {totalUploading > 0 && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-blue-500 text-white py-2 px-4 shadow-lg">
          <div className="max-w-2xl mx-auto flex items-center justify-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
            <span className="font-medium">
              📤 Envoi de {totalUploading} photo{totalUploading > 1 ? 's' : ''} en cours...
            </span>
          </div>
        </div>
      )}
      
      {/* Header fixe en haut */}
      <div className={`sticky z-40 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 shadow-lg ${totalUploading > 0 ? 'top-10' : 'top-0'}`}>
        <div className="max-w-2xl mx-auto">
          {/* Logo */}
          <div className="flex justify-center mb-3">
            {(data?.branding?.logo_url) ? (
              <img
                src={data.branding.logo_url}
                alt={data.branding.company_name || ''}
                className="h-14 w-auto object-contain"
              />
            ) : null}
          </div>
          
          <h1 className="text-xl font-bold mb-1 text-center">
            {getGreeting()}
          </h1>
          <p className="text-blue-100 text-sm text-center">
            📅 {new Date(mission_date).toLocaleDateString('fr-FR', {
              weekday: 'long',
              day: 'numeric',
              month: 'long'
            })}
          </p>
          <p className="text-xs text-blue-200 mt-1 text-center">
            {missions.length} logement{missions.length > 1 ? 's' : ''} aujourd&apos;hui
          </p>
          
          {/* Citation du jour - affichée seulement si pas de média */}
          {dailyCitation && dailyCitation.citation && !employeeMedia && (
            <div className="mt-3 bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-3 border border-white border-opacity-30">
              <p className="text-center italic text-white text-base leading-relaxed font-medium">
                &ldquo;{dailyCitation.citation.texte}&rdquo;
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ============================================ */}
      {/* SECTION MÉDIA EMPLOYÉ (Photo/GIF/Vidéo) */}
      {/* ============================================ */}
      {employeeMedia && employeeMedia.media_data && (
        <div className="max-w-2xl mx-auto px-3 mt-4">
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            {employeeMedia.media_type === 'video' ? (
              /* Vidéo - Avec bouton de lecture */
              <div className="relative">
                {!showMediaVideo ? (
                  <div 
                    onClick={() => setShowMediaVideo(true)}
                    className="relative cursor-pointer group"
                  >
                    <video 
                      src={employeeMedia.media_data}
                      className="w-full h-auto max-h-80 object-contain bg-gray-900"
                      muted
                      playsInline
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 group-hover:bg-opacity-20 transition-all">
                      <div className="w-20 h-20 bg-white bg-opacity-90 rounded-full flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform">
                        <span className="text-purple-600 text-4xl ml-1">▶</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <video 
                    src={employeeMedia.media_data}
                    className="w-full h-auto max-h-80 object-contain bg-gray-900"
                    controls
                    autoPlay
                    onEnded={() => setShowMediaVideo(false)}
                  />
                )}
              </div>
            ) : (
              /* Photo ou GIF - Affichage statique ou animé */
              <img 
                src={employeeMedia.media_data} 
                alt="Message personnalisé"
                className="w-full h-auto max-h-80 object-contain bg-gray-100"
              />
            )}
          </div>
        </div>
      )}

      {/* Liste des missions */}
      <div className="max-w-2xl mx-auto p-3 space-y-4 mt-4">
        {sortedMissions.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-8 text-center">
            <div className="text-6xl mb-4">🎉</div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Aucun logement aujourd&apos;hui
            </h3>
            <p className="text-gray-600">Profitez de votre journée !</p>
          </div>
        ) : (
          <>
            {/* Section Missions Ménage */}
            {missionsMenage.length > 0 && (
              <div className="mb-6">
                {/* Récapitulatif financier */}
                <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-xl p-4 mb-4 shadow-lg">
                  <div className="grid grid-cols-3 gap-3 text-white">
                    <div className="text-center">
                      <p className="text-xs opacity-90 mb-1">💰 Total</p>
                      <p className="text-xl font-bold">{calculateMonthlyTotal().toFixed(0)}€</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs opacity-90 mb-1">✅ Acquis</p>
                      <p className="text-xl font-bold">{calculateCompletedTotal().toFixed(0)}€</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs opacity-90 mb-1">⏳ Reste</p>
                      <p className="text-xl font-bold">{(calculateMonthlyTotal() - calculateCompletedTotal()).toFixed(0)}€</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-blue-100 px-4 py-4 rounded-xl mb-4 border-l-4 border-blue-500">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">🏠</span>
                    <h2 className="text-xl font-bold text-blue-800">
                      Ménage - {new Date(data.mission_date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    </h2>
                  </div>
                </div>
                {missionsMenage.map((mission, index) => {
                  const missionId = mission.id;
                  const housing = mission.housing;
                  const isDivers = false;
                  const gestionParuresActive = housing ? (housing.gestion_parures !== false && housing.type_client !== 'professionnel') : false;
                  const uploadedCount = uploadedPhotos[missionId] || 0;
                  const uploadingCount = uploadingPhotos[missionId] || 0;
                  const totalPhotos = uploadedCount + uploadingCount;
                  const isExpanded = expandedMissions[missionId] === true;
                  const isCompleted = mission.status === 'effectue';
                  
                  return (
                    <div key={missionId} className="bg-white rounded-2xl shadow-lg overflow-hidden mb-3">
                      {/* Header de la carte - Cliquable pour expand/collapse */}
                      <div 
                        onClick={() => toggleMissionExpanded(missionId)}
                        className={`p-4 text-white cursor-pointer transition-all duration-300 ${
                          isCompleted 
                            ? 'bg-gradient-to-r from-green-500 to-emerald-500' 
                            : 'bg-gradient-to-r from-blue-500 to-indigo-500'
                        } hover:opacity-90`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3 flex-1">
                            <span className={`flex-shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full font-bold text-base shadow-md ${
                              isCompleted 
                                ? 'bg-white text-green-600' 
                                : 'bg-white text-blue-600'
                            }`}>
                              {isCompleted ? '✓' : index + 1}
                            </span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <h3 className="font-bold text-base">
                                  {housing.pseudo}
                                  {mission.en_binome && (
                                    <span className="ml-1.5 text-[10px] bg-amber-300/40 text-amber-100 px-1.5 py-0.5 rounded-full font-bold align-middle">👥 Binôme</span>
                                  )}
                                </h3>
                                <span className="text-base font-bold bg-white/20 px-3 py-1 rounded-full">
                                  {(() => {
                                    const prix = getMissionPriceForEmployee({mission, housing});
                                    const bonus = mission.bonus_intervenant || 0;
                                    const total = prix + bonus;
                                    if (total > 0) return `${total.toFixed(0)}€`;
                                    if (housing) return (
                                      <span data-testid="prix-manquant-badge-main" className="text-[11px] bg-amber-200/90 text-amber-900 px-1.5 py-0.5 rounded font-semibold">
                                        0€ · Prix manquant
                                      </span>
                                    );
                                    return '-';
                                  })()}
                                </span>
                              </div>
                              {!isExpanded && (
                                <p className="text-xs text-blue-100">
                                  {housing?.creneau_debut || mission.heure_debut || ''} - {housing?.creneau_fin || mission.heure_fin || ''}
                                </p>
                              )}
                            </div>
                          </div>
                          <span className={`transform transition-transform duration-300 text-xl ${isExpanded ? 'rotate-180' : ''}`}>▼</span>
                        </div>
                      </div>
                      
                      {/* Corps - Affiché seulement si expanded */}
                      {isExpanded && (
                      <div className="p-4 space-y-4">
                        {/* Adresse */}
                        <div className="text-sm text-gray-600">
                          📍 {housing.adresse}, {housing.ville} {housing.code_postal}
                        </div>
                        
                        {/* Horaires */}
                        <div className="text-sm text-gray-600">
                          🕐 {housing?.creneau_debut || mission.heure_debut || 'N/A'} - {housing?.creneau_fin || mission.heure_fin || 'N/A'}
                        </div>
                        
                        {/* Instructions spéciales du logement */}
                        {housing?.instructions && (
                          <div className="bg-red-50 p-3 rounded-lg border-2 border-red-400">
                            <p className="text-sm font-bold text-red-700 mb-1">🔑 Instructions spéciales</p>
                            <p className="text-sm text-red-900 whitespace-pre-wrap">{housing.instructions}</p>
                          </div>
                        )}
                        
                        {/* Lien vers la fiche ménage Google Docs */}
                        {housing?.lien_fiche_menage && (
                          <div className="bg-green-50 p-3 rounded-lg border-2 border-green-400">
                            <p className="text-sm font-bold text-green-700 mb-1">📄 Fiche d&apos;instructions ménage</p>
                            <a
                              href={housing.lien_fiche_menage}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-sm text-green-600 hover:text-green-800 underline font-medium"
                            >
                              📎 Ouvrir la fiche ménage
                            </a>
                          </div>
                        )}
                        
                        {/* Informations complémentaires - reformater les consommables sans prix */}
                        {mission.informations_complementaires && (
                          <div className="bg-blue-50 p-3 rounded-lg border border-blue-300">
                            <p className="text-sm text-blue-800">
                              {mission.informations_complementaires
                                .replace(/🛒 Consommable facturé : /g, '🛒 ')
                                .replace(/\s*\([0-9.,]+€\)/g, '')
                                .replace(/(\S+)\s+(x\d+)(?!\))/g, '$1 ($2)')}
                            </p>
                          </div>
                        )}
                        
                        {/* Notes de la mission */}
                        {mission.notes && (
                          <div className="bg-amber-50 p-3 rounded-lg border border-amber-300">
                            <p className="text-sm font-bold text-amber-700 mb-1">📝 Notes</p>
                            <p className="text-sm text-amber-900">{mission.notes}</p>
                          </div>
                        )}
                        
                        {/* Gestion des parures */}
                        {gestionParuresActive && (
                          <div className="bg-purple-50 p-3 rounded-lg border border-purple-300">
                            <p className="text-sm font-bold text-purple-700 mb-2">🛏️ Parures sales</p>
                            <input
                              type="number"
                              min="0"
                              placeholder="Nombre de parures sales"
                              value={editingParures[missionId] !== undefined ? editingParures[missionId] : (mission.parures_sales || '')}
                              onChange={(e) => handleParuresSalesChange(missionId, e.target.value)}
                              className="w-full px-3 py-2 border rounded-lg text-sm"
                            />
                          </div>
                        )}
                        
                        {/* Contrôle des produits */}
                        <div className="bg-orange-50 p-3 rounded-lg border border-orange-300">
                          <button
                            onClick={() => toggleProductCheck(missionId)}
                            className="w-full flex items-center justify-between text-left"
                          >
                            <span className="text-sm font-bold text-orange-700">
                              🧴 Contrôle des produits
                              {(missingProducts[missionId]?.length || 0) > 0 && (
                                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                                  {missingProducts[missionId].length} manquant(s)
                                </span>
                              )}
                            </span>
                            <span className={`transform transition-transform ${showProductCheck[missionId] ? 'rotate-180' : ''}`}>▼</span>
                          </button>
                          
                          {showProductCheck[missionId] && (
                            <div className="mt-3 space-y-3">
                              <p className="text-sm text-orange-700 font-medium">
                                Cochez les produits manquants ou en quantité insuffisante
                              </p>
                              
                              {Object.entries(getProductsByRoom()).map(([room, products]) => (
                                <div key={room} className="bg-white rounded-lg p-2 border border-orange-200">
                                  <p className="text-xs font-bold text-orange-800 mb-2">{room}</p>
                                  <div className="grid grid-cols-2 gap-1">
                                    {products.map(product => {
                                      const isChecked = (missingProducts[missionId] || []).includes(product);
                                      return (
                                        <label
                                          key={product}
                                          className={`flex items-center gap-2 p-2 rounded cursor-pointer text-xs transition-all ${
                                            isChecked 
                                              ? 'bg-red-100 text-red-700 border border-red-300' 
                                              : 'bg-gray-50 hover:bg-gray-100'
                                          }`}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => toggleMissingProductAuto(missionId, housing?.id, housing?.pseudo, product)}
                                            className="w-4 h-4 accent-red-500"
                                          />
                                          <span className={isChecked ? 'font-medium' : ''}>{product}</span>
                                        </label>
                                      );
                                    })}
                                  </div>
                                </div>
                              ))}
                              
                              {/* Message informatif - pas de bouton commander */}
                              <div className="text-xs text-center text-gray-500 italic py-2">
                                {(missingProducts[missionId]?.length || 0) > 0 
                                  ? `✓ ${missingProducts[missionId].length} produit(s) signalé(s) - Pré-commande envoyée`
                                  : '✓ Cochez les produits manquants pour signaler'
                                }
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Photos */}
                        <div className="bg-gray-50 p-3 rounded-lg border border-gray-300">
                          <p className="text-sm font-bold text-gray-700 mb-2">📷 Photos ({totalPhotos})</p>
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={(e) => handlePhotoCapture(missionId, e)}
                            className="hidden"
                            id={`photo-input-${missionId}`}
                          />
                          <label htmlFor={`photo-input-${missionId}`} className="block w-full py-3 rounded-lg font-bold text-white bg-blue-600 hover:bg-blue-700 text-center cursor-pointer text-sm">
                            📷 Ajouter des photos {uploadingPhotos[missionId] > 0 && `(${uploadingPhotos[missionId]} en cours...)`}
                          </label>
                        </div>
                        
                        {/* Remarques */}
                        <div className="bg-teal-50 p-3 rounded-lg border border-teal-300">
                          <p className="text-sm font-bold text-teal-700 mb-2">💬 Vos remarques</p>
                          <textarea
                            placeholder="Remarques sur le logement..."
                            rows="2"
                            value={editingInformations[missionId] !== undefined ? editingInformations[missionId] : (mission.informations_employe || '')}
                            onChange={(e) => handleInformationsChange(missionId, e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm resize-none"
                          />
                          {/* Bouton transférer remarque à la prochaine mission */}
                          {editingInformations[missionId] && editingInformations[missionId].trim() !== '' && (
                            <button
                              onClick={() => addNoteToNextMission(missionId, housing?.id)}
                              disabled={savingStates[`note_${missionId}`] === 'saving'}
                              className={`mt-2 w-full py-2 rounded-lg font-medium text-sm transition-all ${
                                savingStates[`note_${missionId}`] === 'validated' 
                                  ? 'bg-green-100 text-green-700 border border-green-300'
                                : savingStates[`note_${missionId}`] === 'error' 
                                  ? 'bg-red-100 text-red-700 border border-red-300'
                                : savingStates[`note_${missionId}`] === 'saving' 
                                  ? 'bg-gray-100 text-gray-500 border border-gray-300'
                                : 'bg-purple-100 text-purple-700 border border-purple-300 hover:bg-purple-200'
                              }`}
                            >
                              {savingStates[`note_${missionId}`] === 'validated' ? '✅ Transférée !'
                              : savingStates[`note_${missionId}`] === 'error' ? '❌ Erreur'
                              : savingStates[`note_${missionId}`] === 'saving' ? '⏳ Envoi...'
                              : '📋 Transférer à la prochaine mission'}
                            </button>
                          )}
                        </div>
                        
                        {/* Bouton Mission effectuée */}
                        <button
                          onClick={() => markMissionCompleted(missionId, mission, housing)}
                          disabled={savingStates[`completed_${missionId}`] === 'saving'}
                          className={`w-full py-3 rounded-lg font-bold text-white text-sm ${
                            savingStates[`completed_${missionId}`] === 'validated' ? 'bg-green-500'
                            : savingStates[`completed_${missionId}`] === 'error' ? 'bg-red-500'
                            : savingStates[`completed_${missionId}`] === 'saving' ? 'bg-gray-400'
                            : 'bg-blue-600 hover:bg-blue-700'
                          }`}
                        >
                          {savingStates[`completed_${missionId}`] === 'validated' ? '✅ Effectuée'
                          : savingStates[`completed_${missionId}`] === 'error' ? '❌ Erreur'
                          : savingStates[`completed_${missionId}`] === 'saving' ? '⏳ En cours...'
                          : '✓ Mission effectuée'}
                        </button>
                      </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Section Missions DIVERS */}
            {missionsDivers.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center gap-2 px-3 py-2 bg-amber-100 rounded-lg mb-3 mt-4">
                  <span className="text-xl">📅</span>
                  <h2 className="text-sm font-bold text-amber-800">
                    Autres ({missionsDivers.length})
                  </h2>
                </div>
                {missionsDivers.map((mission, index) => {
                  const missionId = mission.id;
                  const isDivers = true;
                  const uploadedCount = uploadedPhotos[missionId] || 0;
                  const uploadingCount = uploadingPhotos[missionId] || 0;
                  const totalPhotos = uploadedCount + uploadingCount;
                  const isExpanded = expandedMissions[missionId] === true;
                  const isCompleted = mission.status === 'effectue';
                  
                  return (
                    <div key={missionId} className="bg-white rounded-2xl shadow-lg overflow-hidden mb-3">
                      {/* Header */}
                      <div 
                        onClick={() => toggleMissionExpanded(missionId)}
                        className={`p-4 text-white cursor-pointer transition-all duration-300 ${
                          isCompleted 
                            ? 'bg-gradient-to-r from-green-500 to-emerald-500' 
                            : 'bg-gradient-to-r from-amber-500 to-orange-500'
                        } hover:opacity-90`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3 flex-1">
                            <span className={`flex-shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full font-bold text-base shadow-md ${
                              isCompleted ? 'bg-white text-green-600' : 'bg-white text-amber-600'
                            }`}>
                              {isCompleted ? '✓' : (missionsMenage.length + index + 1)}
                            </span>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-bold text-base">{mission.lieu_custom || 'Rendez-vous'}</h3>
                              {!isExpanded && mission.notes && (
                                <p className="text-xs text-amber-100 truncate">{mission.notes}</p>
                              )}
                            </div>
                          </div>
                          <span className={`transform transition-transform duration-300 text-xl ${isExpanded ? 'rotate-180' : ''}`}>▼</span>
                        </div>
                      </div>
                      
                      {/* Corps */}
                      {isExpanded && (
                      <div className="p-4 space-y-4">
                        {/* Horaires */}
                        {(mission.heure_debut || mission.heure_fin) && (
                          <div className="text-sm text-gray-600">
                            🕐 {mission.heure_debut || 'N/A'} - {mission.heure_fin || 'N/A'}
                          </div>
                        )}
                        
                        {/* Notes */}
                        {mission.notes && (
                          <div className="bg-amber-50 p-3 rounded-lg border border-amber-300">
                            <p className="text-sm font-bold text-amber-700 mb-1">📝 Détails</p>
                            <p className="text-base text-amber-900 font-medium">{mission.notes}</p>
                          </div>
                        )}
                        
                        {/* Photos */}
                        <div className="bg-gray-50 p-3 rounded-lg border border-gray-300">
                          <p className="text-sm font-bold text-gray-700 mb-2">📷 Photos ({totalPhotos})</p>
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={(e) => handlePhotoCapture(missionId, e)}
                            className="hidden"
                            id={`photo-divers-${missionId}`}
                          />
                          <label htmlFor={`photo-divers-${missionId}`} className="block w-full py-3 rounded-lg font-bold text-white bg-amber-600 hover:bg-amber-700 text-center cursor-pointer text-sm">
                            📷 Ajouter des photos {uploadingPhotos[missionId] > 0 && `(${uploadingPhotos[missionId]} en cours...)`}
                          </label>
                        </div>
                        
                        {/* Remarques */}
                        <div className="bg-teal-50 p-3 rounded-lg border border-teal-300">
                          <p className="text-sm font-bold text-teal-700 mb-2">💬 Vos remarques</p>
                          <textarea
                            placeholder="Remarques..."
                            rows="2"
                            value={editingInformations[missionId] !== undefined ? editingInformations[missionId] : (mission.informations_employe || '')}
                            onChange={(e) => handleInformationsChange(missionId, e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm resize-none"
                          />
                        </div>
                        
                        {/* Bouton Mission effectuée */}
                        <button
                          onClick={() => markMissionCompleted(missionId, mission, null)}
                          disabled={savingStates[`completed_${missionId}`] === 'saving'}
                          className={`w-full py-3 rounded-lg font-bold text-white text-sm ${
                            savingStates[`completed_${missionId}`] === 'validated' ? 'bg-green-500'
                            : savingStates[`completed_${missionId}`] === 'error' ? 'bg-red-500'
                            : savingStates[`completed_${missionId}`] === 'saving' ? 'bg-gray-400'
                            : 'bg-amber-600 hover:bg-amber-700'
                          }`}
                        >
                          {savingStates[`completed_${missionId}`] === 'validated' ? '✅ Effectué'
                          : savingStates[`completed_${missionId}`] === 'error' ? '❌ Erreur'
                          : savingStates[`completed_${missionId}`] === 'saving' ? '⏳ En cours...'
                          : '✓ Terminé'}
                        </button>
                      </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
        
        {/* ============================================ */}
        {/* SECTION: RÉCAPITULATIF MENSUEL */}
        {/* ============================================ */}
        
        <div className="max-w-2xl mx-auto px-3 mt-8">
          <div 
            className="bg-purple-600 text-white rounded-t-xl px-4 py-4 font-bold cursor-pointer border-l-4 border-purple-800 shadow-lg"
            onClick={() => setShowMonthlyRecap(!showMonthlyRecap)}
          >
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-3">
                <span className="text-2xl">{showMonthlyRecap ? '▼' : '▶'}</span>
                <span className="text-xl">📊 Récapitulatif mensuel</span>
              </span>
              <div className="flex items-center gap-2">
                <button 
                  onClick={(e) => { e.stopPropagation(); changeMonth(-1); }} 
                  className="px-2 py-1 bg-white/20 rounded hover:bg-white/30"
                >
                  ◀
                </button>
                <span className="text-sm capitalize">{getMonthName()}</span>
                <button 
                  onClick={(e) => { e.stopPropagation(); changeMonth(1); }} 
                  className="px-2 py-1 bg-white/20 rounded hover:bg-white/30"
                >
                  ▶
                </button>
              </div>
            </div>
            {/* Total / Acquis / Reste sous le titre */}
            <div className="flex justify-between mt-3 text-sm">
              <span>Total: <strong>{calculateMonthlyTotal().toFixed(0)}€</strong></span>
              <span>Acquis: <strong className="text-green-300">{calculateCompletedTotal().toFixed(0)}€</strong></span>
              <span>Reste: <strong className="text-yellow-300">{(calculateMonthlyTotal() - calculateCompletedTotal()).toFixed(0)}€</strong></span>
            </div>
          </div>
          
          {showMonthlyRecap && (
            <>
              {/* Liste missions - une ligne par mission */}
              <div className="bg-white rounded-b-xl shadow-md">
                {monthlyMissions.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">Aucune mission ce mois</div>
                ) : (
                  <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
                    {monthlyMissions
                      .sort((a, b) => {
                        const dateA = (a.mission || a).date_mission;
                        const dateB = (b.mission || b).date_mission;
                        return new Date(dateB) - new Date(dateA);
                      })
                      .map((m, idx) => {
                        const mission = m.mission || m;
                        const housing = m.housing || {};
                        const isCompleted = mission.status === 'effectue';
                        const prix = getMissionPriceForEmployee(m);
                        const dateStr = mission.date_mission;
                        const formattedDate = dateStr ? new Date(dateStr).toLocaleDateString('fr-FR', {day: '2-digit', month: '2-digit', year: 'numeric'}) : '-';
                        
                        return (
                          <div 
                            key={mission.id || idx}
                            className={`px-3 py-2 flex items-center ${isCompleted ? 'bg-yellow-100' : 'bg-white'}`}
                          >
                            {/* Date */}
                            <div className={`w-24 text-sm font-medium ${isCompleted ? 'text-yellow-700' : 'text-gray-600'}`}>
                              {formattedDate}
                            </div>
                            {/* Nom du logement */}
                            <div className={`flex-1 text-sm ${isCompleted ? 'text-yellow-800' : 'text-gray-800'}`}>
                              {housing.pseudo || housing.adresse || mission.notes || 'Mission'}
                              {mission.en_binome && (
                                <span className="ml-1 text-[9px] bg-amber-200 text-amber-800 px-1 py-0.5 rounded font-bold">👥</span>
                              )}
                            </div>
                            {/* Prix */}
                            <div className={`text-sm font-semibold ${isCompleted ? 'text-yellow-700' : 'text-blue-600'}`}>
                              {prix > 0 ? (
                                `${prix.toFixed(0)}€`
                              ) : m.housing ? (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-medium" data-testid="prix-manquant-badge">
                                  0€ · Prix manquant
                                </span>
                              ) : (
                                '-'
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        
      </div>
    </div>
  );
};

export default EmployeeDay;
